import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import RightPanel from '../components/RightPanel';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const RisingStarsPage = () => {
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    const handleDoubleClick = () => {
      setShowPanel(false);
    };

    document.addEventListener('dblclick', handleDoubleClick);
    return () => {
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, []);

  const handleHeaderClick = () => {
    setShowPanel(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="明日之星 Rising Stars｜青少年素養與高能動性遊戲化學習沙盒"
        description="明日之星 Rising Stars 是明悅文化專為 0-20 歲新世代設計的遊戲化學習（Game-based Learning）系統，培育高能動性 High Agency、學習力、抗逆力與生涯素養，賦能青少年活出自主成長的人生劇本。"
        path="/rising-stars"
        keywords="明日之星, Rising Stars, 青少年教育, 遊戲化學習, Game-based Learning, 高能動性, High Agency, 賦能, 成長, 學習, 108課綱, 核心素養, SEL, 明悅文化, 明悅心創"
      />
      <Header onHeaderClick={handleHeaderClick} />
      <RightPanel isVisible={showPanel} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-5xl lg:text-6xl font-serif text-gray-900 mb-6">
                明日之星系統（Rising Stars System）
              </h1>
              <h2 className="text-3xl text-orange-600 font-semibold mb-8">
                青少年未來競爭力與素養育成沙盒
              </h2>
              <p className="text-xl text-gray-600 italic">
                The Next-Gen Blueprint & Capability Sandbox
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                專為 <span className="font-bold text-orange-600">0 至 20 歲新世代</span>設計的戰略思維與素養培育系統。明日之星系統打破傳統教條式教育，採用前沿的<span className="font-bold text-orange-600">遊戲化學習（Game Based Learning）</span>架構，模擬青少年成長階段的關鍵轉折。系統著重於「三觀（金錢觀、人生觀、世界觀）」的早期定錨，並在模擬過程中鍛鍊個體的問題解決力、抗壓韌性、精力管理與終身學習力。這是為未來領袖量身打造的認知升級引擎，為其奠定富足喜悅的人生基石。
              </p>
            </div>

            {/* Images 1 & 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cultural-gateway-2/artifacts/oh74wylp_%E6%98%8E%E6%97%A5%E4%B9%8B%E6%98%9F.jpg"
                  alt="明日之星遊戲組件"
                  className="w-full h-auto"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-website-93/artifacts/p91was1w_%E4%BB%80%E9%BA%BC%E6%98%AF%E6%98%8E%E6%97%A5%E4%B9%8B%E6%98%9F.jpg"
                  alt="什麼是明日之星 — 遊戲內容與推演好處"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Positioning Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">產品定位與核心價值</h2>
              <p className="text-2xl text-orange-500 font-medium">Product Positioning & Core Values</p>
            </div>

            <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl p-8 mb-12 text-center">
              <p className="text-2xl font-bold text-white">
                「回應 108 課綱核心素養，打造青少年的自主學習內驅力與適性生涯發展實驗室。」
              </p>
            </div>

            <div className="space-y-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                在當前強調「自發、互動、共好」的教育浪潮中，許多青少年面臨學習動機低落、生涯迷惘或情緒韌性不足的困境。<span className="font-bold text-orange-600">富而喜悅明日之星®系統</span>是一套深耕青少年心理健康與綜合素養提升的「情境式生涯推演平台」。
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                歷經多年嚴謹的教學實踐研發，本系統將學生在成長歷程中面臨的關鍵轉折——如課業挑戰、同儕社交、家庭關係、適性選才、逆境因應等，濃縮於具象化的情境解構地圖中。透過高度參與的<span className="font-bold text-orange-600">體驗式學習（Experiential Learning）</span>，學生在模擬的生命歷程中勇敢做出決策、直面挫折、規劃高教發展，為教育現場提供一個高效、可複製且具永續性的素養育成解決方案。
              </p>
            </div>

            {/* Core Educational Value */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-orange-600 mb-6">核心教育價值 (Core Educational Value)：</h3>
              
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 mb-8">
                <h4 className="text-xl font-bold text-gray-800 mb-4">
                  高延展性的教育主題「作業系統」（Educational OS）
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  明日之星最具價值的核心在於其卓越的課程模組彈性。透過多元的情境卡牌組合與專業引導師的引導，能精準對接並解決青少年在不同發展階段的痛點，靈活延伸出五大主題素養工作坊：
                </p>

                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h5 className="text-lg font-bold text-orange-600 mb-2">
                      SEL 社會情緒學習主題（Social Emotional Learning）
                    </h5>
                    <p className="text-gray-700">
                      結合「逆流情境與自我覺察」機制，引導親師生共感情緒困境，深化情感連結與同理心表達。
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h5 className="text-lg font-bold text-orange-600 mb-2">
                      適性學習動機主題（Learning Motivation）
                    </h5>
                    <p className="text-gray-700">
                      將學術探索、素養累積與未來生涯藍圖進行深度關聯，全面喚醒自主學習的內驅力。
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h5 className="text-lg font-bold text-orange-600 mb-2">
                      心理韌性與抗逆力主題（Resilience Training）
                    </h5>
                    <p className="text-gray-700">
                      透過「逆流層與支持系統」的設計，培育青少年面對挫折時的成長型思維（Growth Mindset）與問題解決能力。
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h5 className="text-lg font-bold text-orange-600 mb-2">
                      全方位財商與自我管理主題（Financial & Self-Management）
                    </h5>
                    <p className="text-gray-700">
                      透過資源配置與時間管理的動態模擬，協助建立健康的價值觀與延遲滿足（Delayed Gratification）的能力。
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <h5 className="text-lg font-bold text-orange-600 mb-2">
                      生涯適性規劃主題（Career Pathways）
                    </h5>
                    <p className="text-gray-700">
                      藉由「三層生命視野與願景解構」的推演路徑，引導青少年釐清人生目標，建立長遠的生涯戰略。
                    </p>
                  </div>
                </div>
              </div>

              {/* Application Scenarios */}
              <div>
                <h4 className="text-xl font-bold text-orange-600 mb-4">多元應用場景 (Application Scenarios)：</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow-md border-2 border-orange-200 text-center">
                    <p className="text-gray-800 font-medium">學校輔導室、生涯規劃課、自主學習與彈性學習課程落地</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-md border-2 border-orange-200 text-center">
                    <p className="text-gray-800 font-medium">親職教育講座、家長成長班、班親會互動工作坊</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-md border-2 border-orange-200 text-center">
                    <p className="text-gray-800 font-medium">關注員工照顧（EAP）之企業福利、親子共融企業日</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Mechanics Section */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">系統運作原理</h2>
              <p className="text-2xl text-orange-500 font-medium">System Mechanics & Theoretical Framework</p>
            </div>

            <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl p-8 mb-12 text-center">
              <p className="text-2xl font-bold text-white">
                「以核心素養為底層邏輯，建構『心理—行為—未來』的三維成長模型。」
              </p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-12">
              明日之星系統透過精密的教學設計與<span className="font-bold text-orange-600">遊戲化（Gamification）</span>機制，讓學習在「寓教於樂」的流暢體驗中自然發生。其運作機理主要由以下核心系統支撐：
            </p>

            {/* Images 3 & 4 */}
            <div className="space-y-8 mb-12">
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cultural-gateway-2/artifacts/mpjn1zhy_%E6%98%8E%E6%97%A5%E4%B9%8B%E6%98%9F1.png"
                  alt="成長素養的量化系統"
                  className="w-full h-auto rounded-xl"
                />
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cultural-gateway-2/artifacts/c9b4il58_%E6%98%8E%E6%97%A5%E4%B9%8B%E6%98%9F2.png"
                  alt="逆流與恩典的轉化系統"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </div>

            {/* System Components */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-orange-600 mb-4">
                  三層世界的生涯隱喻
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  系統地圖分為三層，分別投射青少年成長的三種心理狀態與發展階段。從內在心理探索、外在行為實踐，最終對接未來願景，構成一條完整的整合性成長路徑。
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-orange-600 mb-4">
                  引導與反思的深化機制（Reflection & Transfer）
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  系統的教育精髓不僅在於硬體教具，更在於專業引導師的全程陪伴。推演結束後，引導師將帶領參與者進行「深度反思與複盤（Debriefing）」。透過數據回顧、因果思辨與生活遷移應用，將模擬體驗轉化為深刻的生命覺察，讓孩子與家長帶著具體改變，回歸真實的學習與家庭生活中。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multidimensional Value Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">多維價值與社會效益</h2>
              <p className="text-2xl text-orange-500 font-medium">Multidimensional Value & Social Impact</p>
            </div>

            <div className="space-y-12">
              {/* Schools Value */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">🎓</span>
                  <h3 className="text-2xl font-bold text-gray-900">對學校與教育機構的效益</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Value for Schools & Academic Institutions</p>
                
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    實踐「五育並舉」的素養教學活水
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    為學校提供系統化、可定制的素質教育解決方案。將抽象的「核心素養」目標（如學習力、抗逆力、社會情緒力）轉化為可體驗、可量化的彈性課程，有效補充現有學科體系，真正落實「以學生為中心」的素養導向教學。
                  </p>
                </div>
              </div>

              {/* Corporate Value */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💼</span>
                  <h3 className="text-2xl font-bold text-gray-900">對企業發展與員工關懷的效益</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Value for Corporate EAP & Wellbeing</p>
                
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    高附加價值的員工協助方案（EAP）
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    對於注重友善職場與員工福祉的企業，明日之星是極具亮點的員工福利。透過工作坊改善員工的親子溝通、緩解家庭教育帶來的集體焦慮。當員工的家庭關係和諧，將能有效提升職場幸福感、歸屬感與專注度，進而為企業組織注入正向動能。
                  </p>
                </div>
              </div>

              {/* Personal Growth */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">🌱</span>
                  <h3 className="text-2xl font-bold text-gray-900">對個人成長與親職關係的效益</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Value for Personal Growth & Parenting</p>
                
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    親代與子代的雙向覺察之鏡
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    無論是青少年親身體驗，或是家長「換位思考」模擬孩子的視角，明日之星都是一面深刻的內在明鏡。它幫助孩子看見自主發展的潛能與方向，也協助成年人重拾童年記憶、療癒內在，打破世代溝通壁壘，實現親職教育的雙向共融與共同成長。
                  </p>
                </div>
              </div>

              {/* Social Impact */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">🌍</span>
                  <h3 className="text-2xl font-bold text-gray-900">社會價值與永續影響力</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Social Impact & CSR Wellbeing</p>
                
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    直面兒少心理健康議題，啟動社會正向循環
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    精準回應當代青少年抑鬱、焦慮及「拒學/躺平」等重大社會心理議題。明日之星透過遊戲化創新教育模式，為預防兒少心理困境提供了實踐路徑。透過賦能無數家庭與學校，培育具備高度韌性、健全人格與內驅力的下一代，是兼具社會公益價值與創新永續思維的教育貢獻。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RisingStarsPage;
